"use client";

import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { studentAPIService } from "@/lib/student-api.service";
import { useAuth } from "@/global-hooks/useAuth";
import { isReadOnlyUser } from "@/lib/roles";
import { useAppSettings } from "@/hooks/useAppSettings";
import domtoimage from "dom-to-image";
import html2canvas from "html2canvas";
import { domToPng } from "modern-screenshot";
import { User, GraduationCap, CheckCircle2, Clock } from "lucide-react";
import type { Student } from "@/lib/student.schema";
import DocumentList from "./documents-list";
import MssvBarcode from "./mssv-barcode";
import {
	XTTDHQGScores,
	TTBoGDScores,
	UTXDHQGScores,
	CCNNScores,
	DGNLScores,
	HocBaScores,
	THPTQGScores,
} from "./scores";
import { Toast, getSafeErrorMessage } from "@/lib/utils";

interface StudentDetailsProps {
	student: Student;
	autoScreenshot?: boolean;
}

/**
 * Tạm ẩn ô ghi chú khỏi thẻ sinh viên. Đổi thành `true` để hiện lại.
 *
 * Ghi chú đã lưu KHÔNG bị mất: `note` vẫn được nạp từ `student.ghi_chu` khi
 * mount và vẫn đi kèm trong payload cập nhật, nên bấm duyệt lúc đang ẩn sẽ ghi
 * lại đúng giá trị cũ chứ không ghi đè bằng chuỗi rỗng.
 */
const SHOW_NOTE = false;

export default function StudentDetails({ student, autoScreenshot = false }: StudentDetailsProps) {
	const [currentStudent, setCurrentStudent] = useState(student);
	const [documentsReceived, setDocumentsReceived] = useState<string[]>([]);
	const [note, setNote] = useState<string>("");
	const [isUpdating, setIsUpdating] = useState(false);
	const { user } = useAuth();
	const { settings } = useAppSettings();
	const cardRef = useRef<HTMLDivElement>(null);

	// Vai trò chỉ-xem. Đây là lớp GIAO DIỆN: server chặn thật ở `denyReadOnly`
	// trên `PUT /students/:mssv`. Khoá nút ở đây chỉ để người dùng không bấm vào
	// thứ chắc chắn trả 403 — không được coi nó là biện pháp bảo vệ dữ liệu.
	const readOnly = isReadOnlyUser(user);

	// TẠM THỜI — nguồn nhập hiện chỉ cung cấp Truong_THPT_Lop12; lớp 10 và 11
	// rỗng trên toàn bộ hồ sơ (0 / 0 / 4803 trên 4804, kiểm ngày 2026-08-11), nên
	// hai dòng đó không bao giờ hiện và không duyệt được bố cục. Lấy tạm trường
	// lớp 12 làm giá trị hiển thị cho tới khi có dữ liệu thật.
	//
	// Chỉ là hiển thị: không ghi xuống server, không đụng `currentStudent`. Dùng
	// `||` chứ không phải `??` là có chủ ý — nguồn nhập trả chuỗi rỗng chứ không
	// phải null, mà `??` sẽ giữ nguyên chuỗi rỗng. Khi có dữ liệu thật, giá trị
	// thật tự thắng; gỡ hai dòng này và dùng thẳng field.
	const schoolGrade10 = currentStudent.truong_thpt_lop_10 || currentStudent.truong_thpt_lop_12;
	const schoolGrade11 = currentStudent.truong_thpt_lop_11 || currentStudent.truong_thpt_lop_12;

	// Initialize from server data only
	useEffect(() => {
		setCurrentStudent(student);
		setDocumentsReceived(student.ho_so_da_nhan || []);
		setNote(student.ghi_chu || "");
	}, [student]);

	// Comprehensive screenshot function with multiple methods and better timing
	const takeScreenshot = async () => {
		if (!cardRef.current || !autoScreenshot) return;

		// Ensure DOM is fully rendered and styles are applied
		await new Promise((resolve) => setTimeout(resolve, 1000));

		const downloadImage = (dataUrl: string, filename: string) => {
			const link = document.createElement("a");
			link.download = filename;
			link.href = dataUrl;
			link.click();
		};

		const convertBlobToDataUrl = (blob: Blob): Promise<string> => {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});
		};

		// Method 1: Try modern-screenshot (most reliable for complex layouts)
		try {
			console.log("Attempting screenshot with modern-screenshot...");
			const dataUrl = await domToPng(cardRef.current, {
				quality: 1,
				scale: 2,
				backgroundColor: "#ffffff",
			});
			downloadImage(dataUrl, `${currentStudent.mssv}.png`);
			console.log("Screenshot successful with modern-screenshot");
			return;
		} catch (error) {
			console.warn("modern-screenshot failed:", error);
		}

		// Method 2: Try dom-to-image
		try {
			console.log("Attempting screenshot with dom-to-image...");
			const dataUrl = await domtoimage.toPng(cardRef.current, {
				quality: 1.0,
				bgcolor: "#ffffff",
				style: {
					transform: "scale(1)",
					transformOrigin: "top left",
				},
			});
			downloadImage(dataUrl, `${currentStudent.mssv}.png`);
			console.log("Screenshot successful with dom-to-image");
			return;
		} catch (error) {
			console.warn("dom-to-image failed:", error);
		}

		// Method 3: Try html2canvas with enhanced options
		try {
			console.log("Attempting screenshot with html2canvas...");
			const canvas = await html2canvas(cardRef.current, {
				backgroundColor: "#ffffff",
				scale: 2,
				logging: false,
				useCORS: true,
				allowTaint: true,
				foreignObjectRendering: true,
				removeContainer: true,
				imageTimeout: 15000,
				onclone: (clonedDoc: Document) => {
					// Ensure all styles are applied to cloned document
					const clonedElement = clonedDoc.querySelector(
						"[data-radix-popper-content-wrapper]",
					) as HTMLElement;
					if (clonedElement) {
						clonedElement.style.transform = "none";
					}
				},
			});

			canvas.toBlob(
				(blob) => {
					if (blob) {
						convertBlobToDataUrl(blob).then((dataUrl) => {
							downloadImage(dataUrl, `${currentStudent.mssv}.png`);
							console.log("Screenshot successful with html2canvas");
						});
					}
				},
				"image/png",
				1.0,
			);
			return;
		} catch (error) {
			console.warn("html2canvas failed:", error);
		}

		// Method 4: Final fallback - try JPEG with dom-to-image
		try {
			console.log("Attempting final fallback with dom-to-image JPEG...");
			const dataUrl = await domtoimage.toJpeg(cardRef.current, {
				quality: 0.95,
				bgcolor: "#ffffff",
			});
			downloadImage(dataUrl, `${currentStudent.mssv}.jpg`);
			console.log("Screenshot successful with dom-to-image JPEG fallback");
		} catch (error) {
			console.error("All screenshot methods failed:", error);
			alert("Không thể chụp ảnh. Vui lòng thử lại sau hoặc chụp ảnh màn hình thủ công.");
		}
	}; // Helper to get current user info with test user fallback
	const getCurrentUserInfo = () => {
		// Handle test users (Người dùng thử nghiệm)
		if (
			!user ||
			user.email === "admin@gmail.com" ||
			user.email === "admin@example.com" ||
			user.email === "test@example.com"
		) {
			return {
				email: "admin@gmail.com",
				name: "Người dùng thử nghiệm",
				id: "mockUser",
			};
		}

		return {
			email: user.email || "unknown@example.com",
			name: user.name || "Unknown User",
			id: user.id || user.email || "unknown",
		};
	};

	// Helper to get fee status display text (READ-ONLY from API)
	const getFeeStatusDisplay = (feeStatus: string | null | undefined) => {
		if (feeStatus === "PAID") return "Đã đóng học phí";
		if (feeStatus === "UNPAID") return "Chưa đóng học phí";
		return "Chưa có dữ liệu học phí";
	};

	// Helper to get fee status CSS classes (READ-ONLY from API)
	const getFeeStatusClasses = (feeStatus: string | null | undefined) => {
		if (feeStatus === "PAID") return "text-green-700 bg-green-50";
		if (feeStatus === "UNPAID") return "text-red-700 bg-red-50";
		return "text-muted-foreground bg-muted";
	};

	const formatDate = (date: Date | string | null) => {
		if (!date) return "Không có";
		const dateObj = typeof date === "string" ? new Date(date) : date;
		// Format as dd/MM/yyyy with zero-padding for day and month - timezone HCM
		const options: Intl.DateTimeFormatOptions = {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			timeZone: "Asia/Ho_Chi_Minh",
		};
		return dateObj.toLocaleDateString("vi-VN", options);
	};

	const formatDateTime = (date: Date | string | null) => {
		if (!date) return "Không có";
		const dateObj = typeof date === "string" ? new Date(date) : date;

		// Format as HH:mm dd/MM/yyyy - timezone Asia/Ho_Chi_Minh
		const options: Intl.DateTimeFormatOptions = {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			timeZone: "Asia/Ho_Chi_Minh",
		};
		return dateObj.toLocaleString("vi-VN", options);
	};

	const getGenderBadge = (gender: string) => {
		return gender === "Nam" ? "bg-blue-100 text-blue-800" : "bg-pink-100 text-pink-800";
	};

	const getApprovalBadge = (status: string) => {
		// Treat anything not explicitly "approved" as pending
		if (status === "approved") {
			return (
				<Badge className="bg-green-100 text-green-800 flex items-center gap-1 text-sm px-3 py-1">
					<CheckCircle2 className="h-4 w-4" /> Đã tiếp nhận
				</Badge>
			);
		}
		// pending (also covers unknown/deprecated values after mapping)
		return (
			<Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1 text-sm px-3 py-1">
				<Clock className="h-4 w-4" /> Chờ tiếp nhận
			</Badge>
		);
	};

	/**
	 * Mở hộp thoại in và chờ nó đóng lại.
	 *
	 * Trình duyệt không cho biết người dùng đã in hay bấm huỷ — sự kiện
	 * `afterprint` bắn giống nhau ở cả hai trường hợp (HTML spec cố ý không
	 * phân biệt). Ở đây chỉ cần biết hộp thoại đã đóng để tiếp tục gọi API.
	 */
	const printAndWait = () =>
		new Promise<void>((resolve) => {
			const finish = () => {
				window.removeEventListener("afterprint", finish);
				resolve();
			};

			window.addEventListener("afterprint", finish);
			window.print();
			// Trên desktop `window.print()` chặn cho tới khi hộp thoại đóng, nên tới
			// đây là đã xong; timeout là lối thoát cho trình duyệt không bắn afterprint.
			setTimeout(finish, 0);
		});

	const handleApproval = async (status: "approved" | "pending") => {
		setIsUpdating(true);

		try {
			const userInfo = getCurrentUserInfo();

			const updateData = {
				UpdateUserId: userInfo.id,
				DaTiepNhan: status === "approved",
				ho_so_da_nhan: status === "approved" ? documentsReceived : [],
				Note: note.trim(),
			};

			const updatedStudent = await studentAPIService.updateStudentByMSSV(
				currentStudent.mssv,
				updateData,
			);

			// Update current student state with response from API
			//
			// `flushSync` chứ không phải `setCurrentStudent` trần: hộp thoại in ngay
			// bên dưới đọc DOM tại chỗ, mà một `setState` thường chỉ được commit sau
			// khi handler này nhường quyền lại cho React. Không ép commit thì giấy in
			// ra mang đúng bản CŨ — "CB thay đổi: Chưa có", "Chỉnh sửa lúc: Chưa có"
			// và badge trạng thái cũ — dù API đã trả về thành công.
			flushSync(() => setCurrentStudent(updatedStudent));

			// In SAU khi cập nhật, không phải trước: tờ giấy là bằng chứng tiếp nhận
			// nên phải mang tên người tiếp nhận, thời điểm tiếp nhận và trạng thái
			// thật. In trước thì ba thứ đó đều chưa tồn tại.
			//
			// Hệ quả có chủ ý: API lỗi thì không in gì cả, thay vì in ra một tờ giấy
			// xác nhận việc chưa hề được ghi nhận. Admin vẫn tắt được bước in qua cài
			// đặt hệ thống (`allowPrintBeforeCompletion`). Hoàn tác thì không in.
			if (status === "approved" && settings.allowPrintBeforeCompletion) {
				await printAndWait();
			}

			// KHÔNG toast khi thành công — cố ý, và đây là lý do.
			//
			// Toaster sống 4 giây (`App.tsx`) và không có luật `@media print` nào ẩn
			// nó. Chuyên viên tiếp nhận làm liên tục hết sinh viên này tới sinh viên
			// khác, nên toast của hồ sơ TRƯỚC còn nguyên trên màn hình lúc hộp thoại
			// in của hồ sơ SAU mở ra, và nó bị in đè lên tờ A4 — tờ giấy mang tên một
			// sinh viên nhưng có dòng thông báo của sinh viên khác.
			//
			// Không mất phản hồi nào: badge trạng thái đổi ngay, khối "CB tiếp nhận"
			// điền tên và giờ, và với thao tác tiếp nhận thì chính tờ giấy in ra là
			// xác nhận. Nhánh lỗi bên dưới VẪN toast — thất bại phải nói ra được, và
			// toast lỗi không bao giờ trùng với lúc in vì lỗi thì không in gì cả.

			// Take screenshot if auto-screenshot is enabled
			if (autoScreenshot && status === "approved") {
				// Wait longer to ensure all DOM updates are complete
				setTimeout(() => {
					takeScreenshot();
				}, 1000);
			}
		} catch (error) {
			console.error("Error updating student:", error);

			// Check if it's an authentication error
			if (error instanceof Error && error.message.includes("Authentication required")) {
				console.warn("Authentication failed during approval, user will be redirected to login");
				// Don't update local state on auth error - user will be redirected
				return;
			}

			// KHÔNG set state lạc quan ở đây nữa. Trước đây nhánh này ghi trạng thái
			// mới vào màn hình dù server đã TỪ CHỐI, nên badge xanh lên y hệt lúc
			// thành công. Khi còn toast thành công thì "không thấy toast" là dấu hiệu
			// duy nhất phân biệt hai trường hợp; bỏ toast đi mà giữ đoạn này thì một
			// hồ sơ lưu hỏng trông giống hệt một hồ sơ đã tiếp nhận, và không ai biết
			// để làm lại.
			//
			// Giữ nguyên `currentStudent` là nói thật: máy chủ chưa ghi nhận gì.
			Toast.error(
				`Không lưu được hồ sơ ${currentStudent.mssv}: ${getSafeErrorMessage(error)}. Vui lòng thử lại.`,
			);
		} finally {
			setIsUpdating(false);
		}
	};

	// Tên ngành thường có phần chi tiết trong ngoặc, ví dụ
	// "Nhóm ngành máy tính (ngành Công nghệ thông tin; ...)" — tách xuống dòng riêng.
	const parenIndex = currentStudent.ten_nganh?.indexOf("(") ?? -1;
	const tenNganhChinh =
		parenIndex > 0
			? currentStudent.ten_nganh.slice(0, parenIndex).trim()
			: currentStudent.ten_nganh;
	const tenNganhChiTiet = parenIndex > 0 ? currentStudent.ten_nganh.slice(parenIndex).trim() : "";

	return (
		<div className="h-full overflow-hidden">
			<Card
				className="h-full screenshot-target"
				ref={cardRef}
				style={{
					position: "relative",
					backgroundColor: "#ffffff",
					minHeight: "100%",
					transform: "translateZ(0)", // Force hardware acceleration for better rendering
				}}
			>
				<CardContent className="p-4 h-full" style={{ backgroundColor: "#ffffff" }}>
					{/* Header Section - Fixed */}
					<div className="mb-4 pb-3 border-b">
						{/* Ba khối (họ tên, mã vạch, badge) nằm ngang từ md trở lên. Dưới md
						    chúng xếp dọc: gộp lại trên một hàng 390px thì mã vạch và badge bị
						    đẩy ra ngoài mép thẻ, đúng như ảnh chụp trước khi sửa. */}
						<div className="flex flex-col gap-3 mb-2 md:flex-row md:items-start md:justify-between md:gap-0">
							<div className="flex-1 min-w-0">
								<h2 className="text-xl md:text-2xl font-bold mb-1 break-words">
									{/* changed: color the id and name */}
									<span className="text-blue-800">{currentStudent.mssv}</span>
									<span className="text-blue-800 mr-2 ml-2">-</span>
									<span className="text-blue-800">{currentStudent.ho_ten}</span>
								</h2>
							</div>
							{/* Khoảng trống giữa họ tên và badge trạng thái vốn bỏ không. `flex-1`
							    để vùng này nhận đúng phần trống đó rồi căn giữa mã vạch trong nó,
							    thay vì dán mã vạch sát badge. Trên mobile mã vạch căn trái cho
							    thẳng hàng với phần còn lại của thẻ.
							    `max-w-full` để mã vạch (SVG có width cố định do JsBarcode đặt) co
							    lại vừa bề ngang máy thay vì tràn ra ngoài. */}
							<div className="flex justify-start md:flex-1 md:justify-center">
								<MssvBarcode mssv={currentStudent.mssv} className="max-w-full h-auto" />
							</div>
							<div className="flex flex-row items-end gap-2">
								{getApprovalBadge(currentStudent.trang_thai_duyet)}
							</div>
						</div>
					</div>

					{/* Main Content Grid - 4 columns for maximum info.
					    `divide-x` kẻ vạch dọc giữa bốn cột như một splitter: bốn khối thông
					    tin khác loại nằm sát nhau, không có ranh giới thì mắt đọc lẫn từ cột
					    này sang cột kia — nhất là khi thẻ sinh viên được chụp ảnh và in.
					    Vạch tự cao bằng cột cao nhất vì grid item mặc định `stretch`.
					    Bỏ khoảng cách NGANG (`gap-y-4`, không `gap-4`) là có chủ ý: còn gap
					    ngang thì vạch nằm ở mép cột nên trông lệch hẳn về cột bên phải thay
					    vì ở giữa hai cột; khoảng thở lấy từ `px-3` của từng cột.
					    Component điểm chỉ render MỘT khối theo diện xét tuyển, các khối còn
					    lại trả `null` nên không sinh phần tử DOM — do đó không có vạch thừa.
					    `divide-x-2` + `divide-border` thay cho vạch 1px màu mặc định: vạch
					    nhạt gần như biến mất khi thẻ sinh viên được in ra giấy hoặc xem trên
					    màn hình sáng, tức không làm được đúng việc mà nó có mặt để làm.

					    Lưới 12 cột chỉ bật từ lg (1024px) trở lên. Dưới ngưỡng đó bốn khối xếp
					    dọc một cột: nhồi cả bốn vào 390px thì mỗi cột còn ~90px, chữ vỡ từng
					    chữ cái và các cột đè lên nhau; ngay ở 768px thì nút "Tiếp nhận" cũng
					    đã bị cắt mất chữ. Ranh giới đổi hướng theo — vạch ngang giữa các khối
					    khi xếp dọc, vạch dọc khi nằm ngang.
					    Bộ `print:` lặp lại đúng giá trị của `lg:` để tờ A4 luôn là 4 cột, bất
					    kể bề ngang giấy sau khi trừ lề có vượt 1024px hay không (người dùng
					    đổi được lề trong hộp thoại in). Hai bộ luôn cùng giá trị nên chúng
					    không bao giờ mâu thuẫn, thứ tự sinh CSS ra sao cũng không đổi kết quả. */}
					<div className="grid grid-cols-1 gap-y-4 divide-y-2 lg:grid-cols-12 lg:divide-y-0 lg:divide-x-2 print:grid-cols-12 print:divide-y-0 print:divide-x-2 divide-border [&>*]:px-3">
						{/* Column 1: Personal Info (3 cols - reduced from 4) */}
						<div className="lg:col-span-3 print:col-span-3 space-y-3">
							<div className="space-y-2">
								<div className="flex items-center gap-2 mb-2">
									<User className="h-5 w-5 text-blue-600" />
									<span className="font-semibold text-lg text-blue-800">Thông tin cá nhân</span>
								</div>
								{/* text-lg (18px) thay vì text-base: cán bộ tiếp nhận đọc thẻ này cả
								    ngày và phần lớn đã có tuổi, nên khối thông tin cá nhân ưu tiên
								    dễ đọc hơn là gọn. */}
								<div className="space-y-1.5 text-lg">
									<div className="flex justify-between">
										<span className="text-muted-foreground font-medium">Giới tính:</span>
										<Badge
											className={getGenderBadge(currentStudent.gioi_tinh)}
											// Cỡ chữ đặt thẳng ở đây nên không thừa hưởng text-lg của khối —
											// phải nâng cùng, nếu không badge sẽ nhỏ hơn hẳn các dòng quanh nó.
											style={{ fontSize: "18px", padding: "2px 6px" }}
										>
											{currentStudent.gioi_tinh}
										</Badge>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground font-medium">Ngày sinh:</span>
										<span className="font-semibold text-blue-700">
											{formatDate(currentStudent.ngay_sinh)}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground font-medium">Khu vực:</span>
										<span className="font-semibold text-blue-700">
											{currentStudent.khu_vuc ? currentStudent.khu_vuc : "Không"}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground font-medium">Đối tượng:</span>
										{/* changed: add color */}
										<span className="font-semibold text-blue-700">
											{currentStudent.doi_tuong ? currentStudent.doi_tuong : "Không"}
										</span>
									</div>
									<div className="flex justify-between items-start gap-2">
										<span className="text-muted-foreground font-medium whitespace-nowrap">
											Ngành học:
										</span>
										<span className="text-right">
											<span className="block font-semibold text-purple-700">
												{currentStudent.ma_nganh} - {tenNganhChinh}
											</span>
											{tenNganhChiTiet && (
												<span className="block text-base font-medium text-purple-500">
													{tenNganhChiTiet}
												</span>
											)}
										</span>
									</div>
								</div>

								{/* Note Section */}
								{SHOW_NOTE && (
									<div className="space-y-2">
										<label className="text-sm font-medium text-muted-foreground">Ghi chú:</label>
										<textarea
											value={note}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
												setNote(e.target.value)
											}
											placeholder="Nhập ghi chú..."
											className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
											disabled={currentStudent.trang_thai_duyet === "approved"}
										/>
									</div>
								)}

								{/* CB Information Section */}
								{/* text-lg cùng cỡ với khối thông tin cá nhân phía trên — khối này
								    trước đây là text-sm (14px), nhỏ hơn cả cỡ cũ của khối kia, nên
								    nâng riêng thì hai khối trong cùng một cột mới đọc như nhau. */}
								<div className="space-y-2 text-lg">
									<div className="flex items-center gap-2 mb-2">
										<User className="h-5 w-5 text-indigo-600" />
										<span className="font-semibold text-lg text-indigo-800">CB tiếp nhận</span>
									</div>
									<div className="space-y-1.5">
										<div className="flex justify-between">
											<span className="text-muted-foreground font-medium">CB thay đổi:</span>
											{/* changed: color for approver name */}
											<span className="font-medium text-indigo-700 text-right">
												{currentStudent.nguoi_duyet || "Chưa có"}
											</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground font-medium">Chỉnh sửa lúc:</span>
											{/* changed: color for approver date and use datetime format */}
											<span className="font-medium text-indigo-600 text-right">
												{currentStudent.nguoi_duyet
													? formatDateTime(currentStudent.ngay_duyet)
													: "Chưa có"}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Column 2: Academic Info (4 cols - increased from 3) */}
						<div className="lg:col-span-4 print:col-span-4 space-y-3">
							<div className="space-y-2">
								<div className="flex items-center gap-2 mb-2">
									<GraduationCap className="h-5 w-5 text-purple-600" />
									<span className="font-semibold text-lg text-purple-800">Học tập</span>
								</div>
							</div>

							<div className="space-y-2">
								{schoolGrade10 && (
									<div className="flex justify-between items-start gap-2">
										<span className="text-muted-foreground font-medium whitespace-nowrap">
											Trường lớp 10:
										</span>
										<span className="font-medium text-right text-foreground">{schoolGrade10}</span>
									</div>
								)}
								{schoolGrade11 && (
									<div className="flex justify-between items-start gap-2">
										<span className="text-muted-foreground font-medium whitespace-nowrap">
											Trường lớp 11:
										</span>
										<span className="font-medium text-right text-foreground">{schoolGrade11}</span>
									</div>
								)}
								<div className="flex justify-between items-start gap-2">
									<span className="text-muted-foreground font-medium whitespace-nowrap">
										Trường lớp 12:
									</span>
									<span className="font-medium text-right text-foreground">
										{currentStudent.truong_thpt_lop_12}
									</span>
								</div>
								<div className="flex justify-between items-start gap-2">
									<span className="text-muted-foreground font-medium whitespace-nowrap">
										Năm tốt nghiệp:
									</span>
									<span className="font-medium text-right text-foreground">
										{currentStudent.nam_tot_nghiep}
									</span>
								</div>
							</div>

							<div className="flex justify-between">
								<span className="text-muted-foreground font-medium">Phương thức trúng tuyển:</span>
								<span className="font-small text-right text-blue-700">
									{currentStudent.dien_trung_tuyen}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground font-medium">Trúng tuyển nhờ Ưu tiên:</span>
								{currentStudent.trung_tuyen_uu_tien &&
								currentStudent.trung_tuyen_uu_tien.trim() !== "" ? (
									<span className="bg-yellow-300 text-indigo-800 px-2 py-1 rounded text-sm font-semibold text-right break-words max-w-[60%]">
										{currentStudent.trung_tuyen_uu_tien}
									</span>
								) : (
									<span className="font-medium text-right text-foreground">Không</span>
								)}
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground font-medium">Giải thưởng:</span>
								<span className="font-small text-right text-blue-700">
									{currentStudent.giai_thuong ? currentStudent.giai_thuong : "Không có"}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground font-medium">Chứng chỉ NN:</span>
								<span className="font-small text-right text-blue-700">
									{currentStudent.chung_chi ? currentStudent.chung_chi : "Không có"}
								</span>
							</div>
						</div>

						{/* Column 3: Scores */}
						{currentStudent.dien_trung_tuyen === "XTT_DHQG" ? (
							<XTTDHQGScores student={currentStudent} />
						) : currentStudent.dien_trung_tuyen === "XTT_BoGD" ||
						  currentStudent.dien_trung_tuyen === "UTX_BoGD" ? (
							<TTBoGDScores student={currentStudent} />
						) : currentStudent.dien_trung_tuyen === "UTX_DHQG" ? (
							<UTXDHQGScores student={currentStudent} />
						) : currentStudent.dien_trung_tuyen === "NNQT" ? (
							<CCNNScores student={currentStudent} />
						) : currentStudent.dien_trung_tuyen === "DGNL" ? (
							<DGNLScores student={currentStudent} />
						) : currentStudent.dien_trung_tuyen === "THPT" ? (
							<THPTQGScores student={currentStudent} />
						) : (
							/* Mặc định, KHÔNG phải `null`. Các nhánh trên là bộ mã diện của mùa
							   2025; mùa 2026 đổi sang `DGNL_HB`, `THPT_HB`, `XTT`, `UTXT`, nên khi
							   dữ liệu 2026 vào thì mọi sinh viên rơi xuống đây — trước đây là
							   `null`, tức 5.159 hồ sơ không hiện khối điểm nào.
							   Học bạ 3 năm là dữ liệu mọi sinh viên đều có, nên rơi về nó luôn
							   đúng hơn là không hiện gì, và một mã diện mới trong tương lai cũng
							   không làm trang trắng nữa. */
							<HocBaScores student={currentStudent} />
						)}

						{/* Column 4: Actions & Documents (3 cols) */}
						<div className="lg:col-span-2 print:col-span-2 space-y-3">
							{/* Action Buttons at Top.
							    Vai trò chỉ-xem không render nút ghi nào, chỉ hiện trạng thái kèm
							    một câu nói rõ vì sao.
							    Ẩn chứ không `disabled`: một nút mờ suốt cả phiên khiến người dùng
							    chờ đến lúc nó bấm được, còn ở đây quyền sẽ không bao giờ có. */}
							<div className="space-y-2">
								{readOnly ? (
									<div className="text-center p-2 bg-muted rounded-lg border">
										<div className="font-semibold text-muted-foreground text-base">
											{currentStudent.trang_thai_duyet === "approved"
												? "✅ Đã tiếp nhận"
												: "Chưa tiếp nhận"}
										</div>
										<div className="text-xs text-muted-foreground mt-1">
											Tài khoản chỉ xem — không thể thay đổi hồ sơ
										</div>
									</div>
								) : currentStudent.trang_thai_duyet === "approved" ? (
									<div className="space-y-2">
										<div className="text-center p-2 bg-green-50 rounded-lg border">
											<div className="font-semibold text-green-800 text-base">✅ Đã tiếp nhận</div>
										</div>
										<Button
											onClick={() => handleApproval("pending")}
											variant="outline"
											className="w-full border-yellow-600 text-yellow-700 hover:bg-yellow-50 py-2 text-sm"
											disabled={isUpdating}
										>
											<Clock className="h-4 w-4 mr-1" />
											{isUpdating ? "Đang xử lý..." : "Hoàn tác tiếp nhận"}
										</Button>
									</div>
								) : (
									<div className="flex gap-2">
										<Button
											onClick={() => handleApproval("approved")}
											className="w-full bg-green-700 hover:bg-green-800 text-white py-3 text-base font-semibold"
											disabled={isUpdating}
										>
											<CheckCircle2 className="h-5 w-5 mr-2" />
											{isUpdating ? "Đang tiếp nhận..." : "Tiếp nhận"}
										</Button>
									</div>
								)}
							</div>

							{/* Fee Status UI */}
							<div className="text-center rounded-lg border">
								<div
									className={`text-sm rounded-lg font-semibold p-2 ${getFeeStatusClasses(currentStudent.tinh_trang_hoc_phi)}`}
								>
									{getFeeStatusDisplay(currentStudent.tinh_trang_hoc_phi)}
								</div>
							</div>

							{/* Documents List */}
							{/* `readOnly` truyền xuống chứ không để `DocumentList` tự đọc `useAuth`:
						    thành phần đó đã nhận trạng thái khoá qua prop cho trường hợp hồ sơ đã
						    duyệt, nên hai nguồn khoá đi cùng một đường là đọc được. */}
							<DocumentList
								student={currentStudent}
								onDocumentsChange={setDocumentsReceived}
								readOnly={readOnly}
							/>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
